import { UploadSource } from "@/components/upload/types/upload-item";
import type { GraphCallOptions } from "@/services/microsoft/graphApiService";
import {
  WorkspaceDocument,
  WorkspaceFolder,
} from "@/models/workspace/Workspace";

/**
 * Represents a folder name update detected during sync
 */
export type FolderNameUpdate = {
  folder: WorkspaceFolder;
  newName: string;
};

/**
 * Metadata for a file from SharePoint/OneDrive
 */
export type CloudFileMetadata = {
  source: UploadSource;
  driveId?: string;
  siteId?: string;
  sharepointFileId: string;
};

/**
 * Information about a new document detected during sync
 */
export type NewDocumentInfo = {
  /** Name on SharePoint/OneDrive; the content is downloaded when it is uploaded. */
  fileName: string;
  folderPath: string;
  folderMetadata: CloudFileMetadata;
  fileMetadata: CloudFileMetadata;
  parentFolderId?: string;
};

/**
 * Information about a new folder detected during sync (including empty folders)
 *
 * folderPath is relative to the synced workspace folder (parentFolderId).
 */
export type NewFolderInfo = {
  folderPath: string;
  metadata: CloudFileMetadata;
  parentFolderId?: string;
};

/**
 * Information about a deleted document detected during sync
 * @deprecated Use MissingCloudDocumentInfo instead
 */
export type DeletedDocumentInfo = {
  document: WorkspaceDocument;
  folderPath: string;
};

/**
 * Status returned when checking whether a cloud file is accessible
 *
 * - `exists`: file is confirmed accessible
 * - `missing`: file returned 404, confirmed not found
 * - `inaccessible`: non-200, non-404 response; could be a permissions error
 * - `unknown`: check could not be completed (network, token, etc.)
 */
export const CloudFileCheckStatus = {
  EXISTS: "exists",
  MISSING: "missing",
  INACCESSIBLE: "inaccessible",
  UNKNOWN: "unknown",
} as const;

export type CloudFileCheckStatus =
  (typeof CloudFileCheckStatus)[keyof typeof CloudFileCheckStatus];

/**
 * Variant of a sync operation result, used for toast/feedback.
 * - default: success or informational
 * - destructive: error
 * - missing: file could not be accessed (e.g. not found on cloud)
 * - failed: SharePoint/OneDrive didn't answer (throttling, an outage, the
 *   network); says nothing about the file, so the user should try again
 */
export const SyncResultVariant = {
  DEFAULT: "default",
  DESTRUCTIVE: "destructive",
  MISSING: "missing",
  FAILED: "failed",
} as const;

export type SyncResultVariant =
  (typeof SyncResultVariant)[keyof typeof SyncResultVariant];

/**
 * Options for syncing one linked document.
 */
export type FileSyncOptions = {
  graphOptions?: GraphCallOptions;
  /**
   * The cloud file's lastModifiedDateTime (ms) when a folder listing already
   * returned it; an unchanged file then needs no Graph call at all.
   */
  cloudLastModified?: number;
};

/**
 * What fetching a linked document from SharePoint/OneDrive for sync found:
 * - `downloaded`: the cloud copy is newer (or the index needs repair)
 * - `unchanged`: the cloud copy is not newer; nothing was downloaded
 * - `missing`: 404, 403 or another 4xx; the file can't be reached
 * - `failed`: throttling, an outage or the network; try again later
 */
export type CloudFileSyncFetch =
  | { kind: "downloaded"; file: File }
  | { kind: "unchanged" }
  | { kind: "missing" }
  | { kind: "failed"; reason: string };

/**
 * A document in Lara whose cloud counterpart could not be confirmed reachable.
 * Covers both truly deleted files (404) and permission-denied cases.
 */
export type MissingCloudDocumentInfo = {
  document: WorkspaceDocument;
  folderPath: string;
  /** Why it could not be reached */
  status: Exclude<CloudFileCheckStatus, "exists">;
};

/**
 * Aggregated changes identified during the sync identification phase
 */
export type IdentifiedChange = {
  folderNameUpdates: FolderNameUpdate[];
  newFolders: NewFolderInfo[];
  newDocuments: NewDocumentInfo[];
  /** @deprecated Use missingCloudDocuments instead */
  deletedDocuments: DeletedDocumentInfo[];
  /** Documents in Lara whose cloud file is missing or inaccessible */
  missingCloudDocuments: MissingCloudDocumentInfo[];
  existingDocumentsToSync: WorkspaceDocument[];
  /**
   * lastModifiedDateTime (ms) of every file seen in a folder listing, keyed
   * by SharePoint file id. Lets the existing-document sync skip the metadata
   * call for files the listing already shows as unchanged.
   */
  listedFileModified: Map<string, number>;
  /**
   * Folders whose contents couldn't be read completely. Nothing in them is
   * reported missing, since an incomplete listing proves nothing.
   */
  errors: string[];
};

/**
 * A document entry in the sync report with optional owner display name
 */
export type SyncReportDocumentEntry = {
  document: WorkspaceDocument;
  folderPath: string;
  ownerName?: string;
  status: Exclude<CloudFileCheckStatus, "exists">;
};

/**
 * Context about what triggered the sync — used to generate meaningful
 * filenames for CSV exports and report titles.
 * IDs are included so developers can identify exact resources from a
 * customer-submitted report without needing to guess from names.
 */
export type SyncReportContext = {
  workspaceId?: string;
  workspaceName?: string;
  /** Set only when triggered from a specific folder sync */
  folderId?: string;
  folderName?: string;
  /** Display name of the user who initiated the sync */
  initiatedByName?: string;
  initiatedById?: string;
};

/**
 * A document entry in the sync report with folder path and owner (used in per-file lists)
 */
export type SyncReportFileEntry = {
  document: WorkspaceDocument;
  folderPath: string;
  ownerName?: string;
};

/**
 * A newly added document entry — Lara document ID isn't available since
 * handleFolderUpload returns void. SharePoint file ID is used as a proxy.
 * Owner is the user who initiated the sync.
 */
export type SyncReportNewDocEntry = {
  fileName: string;
  folderPath: string;
  /** SharePoint file ID — used as proxy since Lara document ID isn't returned by the upload handler */
  sharepointFileId?: string;
  ownerName?: string;
};

/**
 * Results from executing sync mutations
 */
export type SyncResults = {
  /** Count-only — the report dialog shows these as summary rows */
  existingDocumentsSynced: number;
  existingDocumentsFailed: number;
  existingDocumentsNoUpdate: number;
  folderNamesUpdated: number;
  newFoldersCreated: number;
  newDocumentsAdded: number;
  errors: string[];
  /**
   * Per-file lists — used for the CSV export where individual file detail matters.
   * The dialog still shows the aggregated counts above.
   */
  syncedDocuments: SyncReportFileEntry[];
  noUpdateDocuments: SyncReportFileEntry[];
  /** New files discovered and added from SharePoint/OneDrive during folder sync */
  newDocumentEntries: SyncReportNewDocEntry[];
  /**
   * Documents whose cloud file was missing/inaccessible — for upload-only users.
   * These are shown in the report read-only (no action available).
   */
  skippedMissingDocuments: SyncReportDocumentEntry[];
  /**
   * Documents whose cloud file was missing/inaccessible — for admin/owner users.
   * Shown in the report with an inline delete action.
   */
  missingFilesForAdminReview: SyncReportDocumentEntry[];
  /** Context about what triggered this sync, used in the report title and CSV filename */
  reportContext?: SyncReportContext;
};

/**
 * Graph API child item response structure
 */
export interface GraphApiChildItem {
  id: string;
  name: string;
  lastModifiedDateTime?: string;
  file?: unknown;
  folder?: unknown;
  parentReference?: {
    driveId?: string;
    siteId?: string;
  };
}

/**
 * Graph API response structure for folder children
 */
export interface GraphApiResponse {
  value: GraphApiChildItem[];
  "@odata.nextLink"?: string;
}

/**
 * File information retrieved from SharePoint/OneDrive
 */
export type FetchedFileInfo = {
  id: string;
  name: string;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  source: UploadSource;
  lastModifiedDateTime?: string;
};

/**
 * Folder information retrieved from SharePoint/OneDrive
 */
export type FetchedFolderInfo = {
  id: string;
  name: string;
  driveId?: string;
  siteId?: string;
  sharepointFileId: string;
};

/**
 * Result of fetching folder contents from SharePoint/OneDrive
 */
export type FolderContentsResult = {
  files: FetchedFileInfo[];
  folders: FetchedFolderInfo[];
};

/**
 * Folder metadata retrieved from SharePoint
 */
export type FolderMetadataResult = {
  name: string;
  lastModifiedDateTime?: string;
} | null;

/**
 * Options for sync mutation execution
 */
export type SyncMutationOptions = {
  workspaceId: string;
  parentFolderId?: string;
  progressToastId: string | number;
  onProgress?: (message: string, title: string) => void;
};

/**
 * Creates an empty IdentifiedChange object
 */
export const createEmptyIdentifiedChange = (): IdentifiedChange => ({
  folderNameUpdates: [],
  newFolders: [],
  newDocuments: [],
  deletedDocuments: [],
  missingCloudDocuments: [],
  existingDocumentsToSync: [],
  listedFileModified: new Map(),
  errors: [],
});

/**
 * Creates an empty SyncResults object
 */
export const createEmptySyncResults = (): SyncResults => ({
  existingDocumentsSynced: 0,
  existingDocumentsFailed: 0,
  existingDocumentsNoUpdate: 0,
  folderNamesUpdated: 0,
  newFoldersCreated: 0,
  newDocumentsAdded: 0,
  errors: [],
  syncedDocuments: [],
  noUpdateDocuments: [],
  newDocumentEntries: [],
  skippedMissingDocuments: [],
  missingFilesForAdminReview: [],
});

/**
 * Checks if there are any changes to sync
 */
export const hasChangesToSync = (changes: IdentifiedChange): boolean => {
  return (
    changes.folderNameUpdates.length > 0 ||
    changes.newFolders.length > 0 ||
    changes.newDocuments.length > 0 ||
    changes.deletedDocuments.length > 0 ||
    changes.missingCloudDocuments.length > 0 ||
    changes.existingDocumentsToSync.length > 0 ||
    changes.errors.length > 0
  );
};
